import type { Intention } from './AI';
import type { Proposal } from '../lib/Proposal';
import type HexCoords from '../grid/HexCoords';
import type { Bearing } from '../grid/HexBearing';
import type Action from '../lib/Action';
import type { Entity } from './Entity';
import type HexPosition from '../components/HexPosition';

/**
 * World state passed to component methods for making decisions.
 * Contains information about the game world that components need
 * to determine intentions and evaluate proposals.
 */
export interface WorldState {
  /** The player-controlled entity, if one exists */
  player: Entity | null;
  /** Position of the player entity */
  playerPosition: HexPosition | null;
  /** Set of walkable coordinate keys */
  walkableCoords: Set<string>;
  /** Map of occupied coordinates to entities */
  occupiedCoords: Map<string, Entity>;
  /** Map of all valid coordinates on the map */
  allCoords?: Map<string, HexCoords>;
}

/**
 * A node in the pathfinding graph, representing a position and orientation.
 */
export interface PathfindingNode {
  coords: HexCoords;
  bearing: Bearing;
  cost?: number;
}

/**
 * Optional interface for components that provide AI intentions.
 * Components implementing this interface can influence AI decision-making
 * by proposing actions with associated priorities.
 *
 * @example
 * ```typescript
 * class ForwardBite implements IntentionProvider {
 *   getIntention(entity: Entity, worldState: WorldState): Intention | null {
 *     // Check if enemy is in front
 *     // Return high-priority attack intention or null
 *   }
 * }
 * ```
 */
export interface IntentionProvider {
  /**
   * Generates an intention for the entity to act.
   *
   * @param entity - The entity that owns this component
   * @param worldState - Current state of the game world
   * @returns An intention object with priority and action details, or null if no intention
   */
  getIntention(entity: Entity, worldState: WorldState): Intention | null;
}

/**
 * Optional interface for components that propose action outcomes.
 * Components implementing this interface can modify or augment action resolution
 * by providing proposals with effects and animations.
 *
 * Proposals can be REPLACEMENT (override base action) or AUGMENT (add effects).
 * Multiple proposals are merged based on priority, with defensive augments
 * (pure BLOCK effects) applied immediately and other augments deferred until
 * movement is confirmed.
 *
 * @example
 * ```typescript
 * class Shield implements ProposalProvider {
 *   getProposedOutcome(action: Action): Proposal | null {
 *     return {
 *       type: ProposalType.AUGMENT,
 *       priority: 0,
 *       effects: [{ type: 'BLOCK', fromBearings: [0] }]
 *     };
 *   }
 * }
 * ```
 */
export interface ProposalProvider {
  /**
   * Proposes an outcome for the given action.
   *
   * @param action - The action being resolved
   * @returns A proposal object with type, priority, and effects, or null if no proposal
   */
  getProposedOutcome(action: Action): Proposal | null;
}

/**
 * Optional interface for components that provide attack goal positions.
 * Used by pathfinding AI to determine where to move to use abilities,
 * and for visualizing attack patterns to the player.
 *
 * @example
 * ```typescript
 * class ForwardBite implements AttackPositionProvider {
 *   getAttackPositions(entity: Entity, worldState: WorldState) {
 *     // Return positions adjacent to player, facing them
 *     return playerNeighbors.map(hex => ({
 *       coords: hex,
 *       bearing: hex.bearingTo(playerCoords)
 *     }));
 *   }
 *
 *   getAttackPattern(entity: Entity): HexCoords[] {
 *     const selfPos = entity.getComponent(HexPosition);
 *     return selfPos ? [selfPos.coords.neighbor(selfPos.bearing)] : [];
 *   }
 * }
 * ```
 */
export interface AttackPositionProvider {
  /**
   * Returns positions from which this ability can be used (for AI pathfinding).
   *
   * @param entity - The entity that owns this component
   * @param worldState - Current state of the game world
   * @returns Array of position-bearing pairs representing valid attack positions
   */
  getAttackPositions(entity: Entity, worldState: WorldState): { coords: HexCoords, bearing: Bearing }[];

  /**
   * Returns hexes this ability can attack from entity's CURRENT position (for visualization).
   * Pattern is relative to entity's current position and bearing.
   *
   * @param entity - The entity that owns this component
   * @returns Array of absolute HexCoords that can be attacked from current position
   */
  getAttackPattern(entity: Entity): HexCoords[];
}

/**
 * Optional interface for components that provide movement options.
 * Used by pathfinding to explore the movement graph.
 *
 * @example
 * ```typescript
 * class MoveForward implements MoveProvider {
 *   getPossibleMoves(node: PathfindingNode, worldState: WorldState) {
 *     const forward = node.coords.neighbor(node.bearing);
 *     if (worldState.walkableCoords.has(forward.key)) {
 *       return [{ coords: forward, bearing: node.bearing }];
 *     }
 *     return [];
 *   }
 * }
 * ```
 */
export interface MoveProvider {
  /**
   * Returns possible next positions from the given node.
   *
   * @param node - Current position and bearing
   * @param worldState - Current state of the game world (subset for move validation)
   * @returns Array of reachable position-bearing pairs
   */
  getPossibleMoves(node: PathfindingNode, worldState: { walkableCoords: Set<string> }): PathfindingNode[];
}

/**
 * Tag component - empty marker interface.
 * Tag components have no properties or methods; their presence alone carries meaning.
 *
 * @example
 * ```typescript
 * class Dead implements TagComponent {}
 * class PlayerControlled implements TagComponent {}
 * ```
 */
export interface TagComponent {
  // Empty - presence is the data
}

/**
 * Data component - stores properties without behavior methods.
 * Data components have fields but no methods that implement provider interfaces.
 *
 * @example
 * ```typescript
 * class Faction implements DataComponent {
 *   name: string;
 *   constructor(name: string) { this.name = name; }
 * }
 * ```
 */
export interface DataComponent {
  // Has properties but no behavior methods
  [key: string]: any;
}

/**
 * Base component type. Components can be:
 * - Tag components (empty markers)
 * - Data components (properties only)
 * - Provider components (implement one or more provider interfaces)
 * - Hybrid components (implement multiple provider interfaces)
 */
export type Component =
  | TagComponent
  | DataComponent
  | IntentionProvider
  | ProposalProvider
  | AttackPositionProvider
  | MoveProvider
  | DisplayProvider
  | (IntentionProvider & ProposalProvider)  // e.g., OmniBite
  | (IntentionProvider & ProposalProvider & AttackPositionProvider)  // e.g., ForwardBite, RangedAttack

/**
 * Type guard to check if a component implements IntentionProvider.
 * Uses runtime check combined with compile-time type narrowing.
 *
 * @param c - Component to check
 * @returns true if component has getIntention method
 */
export function isIntentionProvider(c: Component): c is IntentionProvider {
  return c !== null && typeof c === 'object' && 'getIntention' in c;
}

/**
 * Type guard to check if a component implements ProposalProvider.
 * Uses runtime check combined with compile-time type narrowing.
 *
 * @param c - Component to check
 * @returns true if component has getProposedOutcome method
 */
export function isProposalProvider(c: Component): c is ProposalProvider {
  return c !== null && typeof c === 'object' && 'getProposedOutcome' in c;
}

/**
 * Type guard to check if a component implements AttackPositionProvider.
 * Uses runtime check combined with compile-time type narrowing.
 *
 * @param c - Component to check
 * @returns true if component has getAttackPositions method
 */
export function isAttackPositionProvider(c: Component): c is AttackPositionProvider {
  return c !== null && typeof c === 'object' && 'getAttackPositions' in c;
}

/**
 * Type guard to check if a component implements MoveProvider.
 * Uses runtime check combined with compile-time type narrowing.
 *
 * @param c - Component to check
 * @returns true if component has getPossibleMoves method
 */
export function isMoveProvider(c: Component): c is MoveProvider {
  return c !== null && typeof c === 'object' && 'getPossibleMoves' in c;
}

/**
 * Information about how a component should be displayed in stat sheets and badges.
 */
export interface DisplayInfo {
  /** Iconify icon name (e.g. 'game-icons:sword-clash') or short text glyph (e.g. '2x') */
  icon: string;
  /** Human-readable label (e.g. 'Forward Bite', 'Speed: 1/2x') */
  label: string;
  /** One-sentence description of what this ability does */
  description: string;
}

/**
 * Optional interface for components that should appear on entity stat sheets.
 * Components implementing this interface will be shown as hex badges in the
 * examine mode info bar and the player's Stats screen.
 *
 * @example
 * ```typescript
 * class ForwardBite implements DisplayProvider {
 *   getDisplayInfo(): DisplayInfo {
 *     return {
 *       icon: 'game-icons:fangs',
 *       label: 'Forward Bite',
 *       description: 'Bites the hex directly ahead.',
 *     };
 *   }
 * }
 * ```
 */
export interface DisplayProvider {
  getDisplayInfo(entity?: Entity): DisplayInfo;
}

/**
 * Type guard to check if a component implements DisplayProvider.
 */
export function isDisplayProvider(c: any): c is DisplayProvider {
  return c !== null && typeof c === 'object' && 'getDisplayInfo' in c;
}
